/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import type {
  AssociatedRegistryType,
  EntityAssociateType,
  EntityRoleType,
} from "@/schemas/digital-files";

interface RegistryFormContextType {
  fieldErrors: Record<string, string | undefined>;
  setFieldError: (fieldId: string, error: string | undefined) => void;
  touchedFields: Record<string, boolean>;
  setFieldTouched: (fieldId: string, touched: boolean) => void;
  isFormValid: boolean;
  fieldValues: Record<string, unknown>;
  setFieldValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  fieldOptions: Record<
    string,
    string | { id: string; table: string; display: string; source: string }
  >;
  setFieldOptions: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        string | { id: string; table: string; display: string; source: string }
      >
    >
  >;
  users: EntityAssociateType[];
  setUsers: React.Dispatch<React.SetStateAction<EntityAssociateType[]>>;
  regName: string;
  setRegName: React.Dispatch<React.SetStateAction<string>>;
  regId: string;
  setRegId: React.Dispatch<React.SetStateAction<string>>;
  registries: AssociatedRegistryType[];
  setRegistries: React.Dispatch<React.SetStateAction<AssociatedRegistryType[]>>;
  rolesList: EntityRoleType[];
  setRolesList: React.Dispatch<React.SetStateAction<EntityRoleType[]>>;
}

const RegistryFormContext = createContext<RegistryFormContextType | undefined>(
  undefined,
);

export const useRegistryForm = () => {
  const context = useContext(RegistryFormContext);
  if (!context) {
    throw new Error("useRegistryForm must be used within RegistryFormProvider");
  }
  return context;
};

export const RegistryFormProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
  const [fieldOptions, setFieldOptions] = useState<
    Record<
      string,
      string | { id: string; table: string; display: string; source: string }
    >
  >({});
  const [users, setUsers] = useState<EntityAssociateType[]>([]);
  const [registries, setRegistries] = useState<AssociatedRegistryType[]>([]);
  const [regName, setRegName] = useState<string>("");
  const [regId, setRegId] = useState<string>("");
  const [rolesList, setRolesList] = useState<EntityRoleType[]>([]);
  const setFieldError = useCallback(
    (fieldId: string, error: string | undefined) => {
      setFieldErrors((prev) => ({
        ...prev,
        [fieldId]: error,
      }));
    },
    [],
  );

  const setFieldTouched = useCallback((fieldId: string, touched: boolean) => {
    setTouchedFields((prev) => ({
      ...prev,
      [fieldId]: touched,
    }));
  }, []);

  const isFormValid = useMemo(() => {
    const hasNoFieldErrors = Object.values(fieldErrors).every(
      (error) => !error,
    );
    const hasValidName = regName.trim() !== "";
    const hasUsers = users.length > 0;
    const hasRoleOptions = rolesList.length > 0;
    const allUsersHaveRoles =
      !hasRoleOptions || users.every((user) => user.role_id);
    return hasNoFieldErrors && hasValidName && (!hasUsers || allUsersHaveRoles);
  }, [fieldErrors, regName, users, rolesList]);

  const value = useMemo(
    () => ({
      fieldErrors,
      setFieldError,
      touchedFields,
      setFieldTouched,
      isFormValid,
      fieldValues,
      setFieldValues,
      fieldOptions,
      setFieldOptions,
      users,
      setUsers,
      regName,
      setRegName,
      regId,
      setRegId,
      registries,
      setRegistries,
      rolesList,
      setRolesList,
    }),
    [
      fieldErrors,
      setFieldError,
      touchedFields,
      setFieldTouched,
      isFormValid,
      fieldValues,
      fieldOptions,
      users,
      regName,
      regId,
      registries,
      setRegistries,
      rolesList,
      setRolesList,
    ],
  );

  return (
    <RegistryFormContext.Provider value={value}>
      {children}
    </RegistryFormContext.Provider>
  );
};
